import { memo, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Users } from 'lucide-react';
import type { StaffRosterEntry } from '../../types/staff';
import { StaffAvatar } from './StaffAvatar';
import { useDiscordPlayer } from '../../hooks/useDiscordPlayer';
import { formatSeconds } from '../../utils/staffProfile';
import { cn } from '../../utils/cn';

interface StaffRosterRailProps {
  staff: StaffRosterEntry[];
  activeDiscordId: string;
  /** Unix seconds the roster was generated. */
  generatedAt: number;
  className?: string;
}

const CONFIDENCE_DOT: Record<string, string> = {
  high: 'bg-green-400',
  medium: 'bg-blue-400',
  low: 'bg-yellow-400',
};

const RosterRow = memo(({ entry, active }: { entry: StaffRosterEntry; active: boolean }) => {
  const { username: mcName } = useDiscordPlayer(entry.discord_id);
  return (
  <li>
    <Link
      to={`/staff/${entry.discord_id}`}
      aria-current={active ? 'page' : undefined}
      title={`${entry.display_name} · @${entry.username}${mcName ? ` · Minecraft: ${mcName}` : ''}${entry.has_profile ? ` · ${entry.confidence} confidence` : ' · no profile yet'}`}
      className={cn(
        'relative flex items-center gap-2.5 pl-3 pr-2.5 py-2 rounded-lg transition-colors touch-manipulation',
        active ? 'bg-accent-500/15' : 'hover:bg-glass-200/10',
      )}
    >
      {active && <span className="absolute left-0 top-2 bottom-2 w-0.5 rounded bg-accent-400 shadow-[0_0_8px_rgb(var(--accent-t-400-rgb)/0.8)]" aria-hidden="true" />}
      <StaffAvatar discordId={entry.discord_id} name={entry.display_name} size="sm" />
      <span className="min-w-0 flex-1">
        <span className={cn('block text-[13px] font-semibold truncate', active ? 'text-accent-300' : 'text-white')}>
          {entry.display_name}
        </span>
        <span className="block text-[11px] font-mono text-glass-500 truncate">{mcName ? mcName : `@${entry.username}`}</span>
      </span>
      <span className="flex flex-col items-end gap-1 flex-shrink-0">
        <span
          className={cn('w-2 h-2 rounded-full', entry.has_profile ? CONFIDENCE_DOT[entry.confidence] ?? 'bg-glass-400' : 'border border-glass-300/40')}
          aria-hidden="true"
        />
        <span className="text-[10px] font-mono text-glass-500 tabular-nums whitespace-nowrap">
          {entry.has_profile ? `${entry.evidence_count} obs` : '—'}
        </span>
      </span>
    </Link>
  </li>
  );
});
RosterRow.displayName = 'RosterRow';

/** Compact roster for switching between staff profiles on wide screens. */
export const StaffRosterRail = ({ staff, activeDiscordId, generatedAt, className }: StaffRosterRailProps) => {
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? staff.filter(
          (s) =>
            s.display_name.toLowerCase().includes(q) ||
            s.username.toLowerCase().includes(q) ||
            s.discord_id.includes(q),
        )
      : staff;
    return [...list].sort((a, b) => Number(b.has_profile) - Number(a.has_profile) || a.display_name.localeCompare(b.display_name));
  }, [staff, query]);
  const profiled = staff.filter((s) => s.has_profile).length;
  const ago = generatedAt ? `${formatSeconds(Date.now() / 1000 - generatedAt)} ago` : null;

  return (
    <aside className={cn('glass-card relative overflow-hidden p-3 flex-col gap-3', className)} aria-label="Staff roster">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent-400/50 to-transparent" aria-hidden="true" />
      <div className="flex items-center justify-between px-1 pt-1">
        <Link to="/staff" className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-glass-400 hover:text-accent-300">
          <Users className="w-4 h-4" aria-hidden="true" />
          Roster
        </Link>
        <span className="text-[11px] font-mono text-glass-500 tabular-nums">
          {profiled}/{staff.length}
        </span>
      </div>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-glass-400" aria-hidden="true" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search staff…"
          aria-label="Search staff"
          className="glass-input w-full pl-9 text-sm"
        />
      </div>
      <ul className="space-y-0.5 max-h-[62vh] overflow-y-auto pr-1 -mr-1">
        {filtered.map((entry) => (
          <RosterRow key={entry.discord_id} entry={entry} active={entry.discord_id === activeDiscordId} />
        ))}
        {filtered.length === 0 && <li className="px-2.5 py-4 text-center text-xs text-glass-500">No staff match.</li>}
      </ul>
      <div className="flex items-center justify-between px-1 text-[10px] font-mono text-glass-500">
        <span className="inline-flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-green-400" /> high
          <span className="w-1.5 h-1.5 rounded-full bg-blue-400" /> med
          <span className="w-1.5 h-1.5 rounded-full bg-yellow-400" /> low
        </span>
        {ago && <span>{ago}</span>}
      </div>
    </aside>
  );
};
